import React from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Card,
  AutoComplete,
  List,
  Popconfirm,
  notification,
  Modal,
  Checkbox,
  Space,
  Alert,
  Tag,
  Tour,
  Spin,
} from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { apiCallPost, apiCallPostCancellable, isBelow40 } from "../fns/util";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
// Define the award tracks
const awardTracks = ["Dr Anadi Bhushan Basu Competition for Posters"];

const terms = [
  "You have no objection to the poster being presented at the annual conference of the Ophthalmological Society of West Bengal or being published in any scientific journal of the Society, if selected.",
  "You have no objection to the poster being presented at the annual conference of the Ophthalmological Society of West Bengal, if selected.",
  "If selected, you will need to submit a presentation of 12 slides, including title slides, covering aims and objectives, materials and methods, results and conclusion. You can include text, figures, tables and images but no videos or animations.",
  "E-Posters will be displayed on monitors at the conference venue. No specific time slots will be needed.",
  "Fonts should be no smaller than 18 pts, and use a maximum of 120 words on each slide. Use a plain background without patterns or dark colours, and use clear headings. Leave spaces between sections. Use labelled, good quality images and tables.",
  "The Scientific Committee of OSWB will have the discretion to rearrange or shift submissions in the best interest of the scientific program of the conference.",
  "If selected, you will be invited to submit your full work via this portal. You will be intimated through email, which will inform you of the deadline for submission of full poster. Failure to meet said deadline will disqualify you from presenting at the conference.",
  "Failure to present the poster at the conference without prior intimation with valid reasons will automatically disqualify the chief author from submitting any work in the subsequent conference.",
  "All presenters will have to register as delegates for the conference.",
  "You are not allowed to change the presenting author except with prior permission and valid reasons.",
  "This work has not submitted for publication, presentation or consideration elsewhere.",
];

class PosterSubmissionForm extends React.Component {
  // Create references for form fields
  refTitle = React.createRef();
  refIntroduction = React.createRef();
  refAuthors = React.createRef();
  refAddAuthor = React.createRef();
  refAwardTrack = React.createRef();
  refTerms = React.createRef();
  refDraft = React.createRef();
  tourSteps = [
    {
      title: "Enter your poster title",
      description:
        "You can enter the title of your poster here. The title can be upto 125 characters long.",
      target: () => this.refTitle.current && this.refTitle.current.input,
    },
    {
      title: "Input your poster structured abstract",
      description:
        "Please input the structured abstract of your poster here, starting with your aims and objectives and then other sections. Note the character limits for each section.",
      target: () =>
        this.refIntroduction.current &&
        this.refIntroduction.current.resizableTextArea.textArea,
    },
    {
      title: "Add authors.",
      description:
        "You can add the authors of the submission here. You must add one presenting author, and up to 4 additional co-authors. Use the search box to search for OSWB members, and add them as authors. Once you have added authors, you can mark who is the presenting author, or delete ones you may have added by error.",
      target: () => this.refAuthors.current,
    },
    {
      title: "Add outside collaborators",
      description:
        "If your co-author is not a member of OSWB, you can enter their name, phone and email, and add them. Remember that per the rules of the conference, only a member can be a presenting author.",
      target: () => this.refAddAuthor.current,
    },
    {
      title: "Select an award track",
      description:
        "Once you have added the presenting author for a submission, you can choose to submit this in competition for a particular award. This is not mandatory, so you can submit without choosing an award track.",
      target: () => this.refAwardTrack.current,
    },
    {
      title: "Read the rules",
      description:
        "Before you submit, make sure you've read the rules. The decision of the scientific committee will be final for all submissions, and if you have any queries, please reach out to us before you submit.",
      target: () => this.refTerms.current && this.refTerms.current.input,
    },
    {
      title: "Save your work",
      description:
        "At any point, if you want, you can save the current state of your submission as a draft. Note that authors added will not be saved in the draft; only your abstract is saved. Once you're ready and you've filled in everything, you can go ahead and submit. Good luck!",
      target: () => this.refDraft.current,
    },
  ];
  colorMap = {
    DRAFT: "orange",
    SUBMITTED: "blue",
    WITHDRAWN: "red",
  };
  // Initialize state
  state = {
    isTourOpen: false,
    termsAgreed: false,
    searchResults: [],
    isSaving: false,
    newUser: { name: "", email: "", phone: "", role: "" },
    title: "",
    content: {
      objectives: "",
      methods: "",
      results: "",
      conclusion: "",
    },
    lifecycle: "DRAFT",
    duration: 0,
    awardTrack: "",
    authors: [],
    presentingAuthorId: "",
    presentingAuthorName: "",
  };
  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
    this.setState({
      lifecycle: this.props.preloadedData.lifecycle,
      title: this.props.preloadedData.title,
      content: this.props.preloadedData.content
        ? this.props.preloadedData.content
        : this.state.content,
      duration: 0,
      awardTrack: this.props.preloadedData.awardTrack,
      authors: this.props.preloadedData.authors,
      presentingAuthorId:
        this.props.preloadedData.presentingAuthorId || undefined,
      presentingAuthorName:
        this.props.preloadedData.presentingAuthorName || undefined,
    });
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }
  handleResize = () => {
    this.setState({ isDesktop: window.innerWidth >= 1000 });
  };
  handleSelectChange = (value) => {
    if (!this.state.presentingAuthorId) {
      notification.error({
        message: "No presenting author selected.",
        description: "Please select a presenting author first.",
      });
      return;
    }
    this.setState({ awardTrack: value });
  };
  handleSearch = async (searchText) => {
    if (!searchText || (searchText && searchText.length <= 2)) {
      return;
    }
    let token = await this.props.context.provideToken();
    try {
      this.setState({ isSearching: true });
      let res = await apiCallPostCancellable(
        token,
        "/conference2023/searchForParticipants",
        { prefix: searchText }
      );
      if (res) {
        this.setState({
          searchResults: res,
          isSearching: false,
        });
      }
    } catch (err) {
      this.setState({ isSearching: false });
    }
  };
  handleAddAuthorFromSearch = async (delegate) => {
    if (this.state.authors.length === 5) {
      notification.error({
        message: "Cannot add more than 5 authors.",
      });
    }
    let authors = this.state.authors;
    if (authors.find((x) => x.delegateId === delegate._id)) {
      notification.error({
        message: "This author is already added.",
        description: "Please select another author.",
      });
      return;
    }
    if (delegate.searchresultsrc === "SRC_CONF") {
      this.setState({
        authors: [
          ...this.state.authors,
          {
            delegateId: delegate._id,
            name: delegate.full_name,
            email: delegate.email,
            mobile: delegate.mobile_number,
            isAgeBelow40: delegate.isAgeBelow40,
            role: "Author",
          },
        ],
        searchResults: [],
      });
      return;
    }
    if (delegate.searchresultsrc === "SRC_OSWB") {
      let token = await this.props.context.provideToken();
      try {
        let res = await apiCallPost(token, "/conference2023/inviteDelegate", {
          name: delegate.full_name,
          email: delegate.email,
          mobile: delegate.mobile_number
            ? "+91" + delegate.mobile_number
            : null,
        });
        this.setState({
          authors: [
            ...this.state.authors,
            {
              delegateId: res.delegate._id,
              name: res.delegate.name,
              email: res.delegate.email,
              mobile: res.delegate.mobile,
              isAgeBelow40: res.delegate.isAgeBelow40,
              role: "Author",
            },
          ],
        });
      } catch (err) {
        notification.error({
          message: "Error adding author.",
        });
      }
    }
  };
  handleAddUser = async () => {
    if (this.state.authors.length === 5) {
      notification.error({
        message: "Cannot add more than 5 authors.",
      });
    }
    this.setState({ showAddUserModal: false, isSaving: true });
    let token = await this.props.context.provideToken();
    try {
      let res = await apiCallPost(token, "/conference2023/inviteDelegate", {
        name: this.state.newUser.name,
        email: this.state.newUser.email,
        mobile: this.state.newUser.phone,
      });
      this.setState({
        isSaving: false,
        authors: [
          ...this.state.authors,
          {
            delegateId: res.delegate._id,
            name: res.delegate.name,
            email: res.delegate.email,
            mobile: res.delegate.mobile,
            isAgeBelow40: res.delegate.isAgeBelow40,
            role: "Author",
          },
        ],
      });
    } catch (err) {
      this.setState({ isSaving: false });
      notification.error({
        message: "Error adding author.",
      });
    }
  };
  handleDeleteUser = (user) => {
    if (user.delegateId === this.state.presentingAuthorId) {
      this.setState({
        presentingAuthorId: "",
        presentingAuthorName: "",
      });
    }
    this.setState((prevState) => ({
      authors: prevState.authors.filter(
        (x) => x.delegateId !== user.delegateId
      ),
    }));
  };
  handleMakePresentingAuthor = (e) => {
    let authors = this.state.authors;
    let idx = _.findIndex(authors, (x) => x.delegateId === e.delegateId);
    authors[idx].role = "Presenting Author";
    let isAgeBelow40 = authors[idx].isAgeBelow40;
    if (this.state.awardTrack === awardTracks[1] && !isAgeBelow40) {
      notification.error({
        message: "Presenting author is not below 40.",
        description: "For this award track, the author needs to be below 40.",
      });
      return;
    }
    // make every other author an author
    authors.forEach((x) => {
      if (x.delegateId !== e.delegateId) {
        x.role = "Author";
      }
    });
    this.setState({
      presentingAuthorId: e.delegateId,
      authors: authors,
      presentingAuthorName: e.name,
    });
  };
  updateAwardTrack = async () => {
    let token = await this.props.context.provideToken();
    try {
      this.setState({ isSaving: true });
      await apiCallPost(token, "/conference2023/editSubmission", {
        submissionId: this.props.preloadedData._id,
        submission: {
          title: this.props.preloadedData.title,
          content: this.props.preloadedData.content,
          awardTrack: this.state.awardTrack,
          authors: this.props.preloadedData.authors,
          presentingAuthorId: this.props.preloadedData.presentingAuthorId,
          presentingAuthorName: this.props.preloadedData.presentingAuthorName,
          duration: this.props.preloadedData.duration,
          lifecycle: this.props.preloadedData.lifecycle,
        },
      });
      notification.success({
        message: "Saved!",
      });
      await apiCallPost(token, "/conference2023/notifyOnSubmit", {
        submissionId: this.props.preloadedData._id,
        suppress: true,
      });
      this.setState({ isSaving: false });
    } catch (err) {
      this.setState({ isSaving: false });
      notification.error({
        message: "Error saving submission.",
      });
    }
  };
  handleSubmit = async () => {
    let token = await this.props.context.provideToken();
    try {
      this.setState({ isSaving: true });
      await apiCallPost(token, "/conference2023/editSubmission", {
        submissionId: this.props.preloadedData._id,
        submission: {
          title: this.state.title,
          content: this.state.content,
          awardTrack: this.state.awardTrack,
          authors: this.state.authors,
          presentingAuthorId: this.state.presentingAuthorId,
          presentingAuthorName: this.state.presentingAuthorName,
          duration: this.state.duration,
          lifecycle: "SUBMITTED",
        },
      });
      this.setState({ isSaving: false, lifecycle: "SUBMITTED" });
      notification.success({
        message: "Submitted!",
      });
      await apiCallPost(token, "/conference2023/notifyOnSubmit", {
        submissionId: this.props.preloadedData._id,
      });
    } catch (err) {
      this.setState({ isSaving: false });
      notification.error({
        message: "Error creating submission.",
      });
    }
  };
  handleSaveDraft = async () => {
    let token = await this.props.context.provideToken();
    try {
      this.setState({ isSaving: true });
      await apiCallPost(token, "/conference2023/editSubmission", {
        submissionId: this.props.preloadedData._id,
        submission: {
          title: this.state.title,
          content: this.state.content,
          awardTrack: this.state.awardTrack,
          authors: this.state.authors,
          presentingAuthorId: this.state.presentingAuthorId,
          presentingAuthorName: this.state.presentingAuthorName,
          duration: this.state.duration,
          lifecycle: "DRAFT",
        },
      });
      this.setState({ isSaving: false });
      notification.success({
        message: "Draft saved.",
      });
    } catch (err) {
      this.setState({ isSaving: false });
      notification.error({
        message: "Error saving draft.",
      });
    }
  };
  renderAwardSelection = () => {
    if (this.state.presentingAuthorId) {
      return (
        <Card
          style={{
            boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
            margin: "auto",
            maxWidth: "800px",
          }}
        >
          {" "}
          <Tag color="blue">Award Track</Tag>
          <p className="small_text">
            You can choose an award track you want to compete in. This is
            optional.
          </p>
          <Form.Item label="Award Track" labelCol={{ span: 24 }}>
            <Select
              onChange={this.handleSelectChange}
              allowClear
              value={this.state.awardTrack}
            >
              <Select.Option value="">None</Select.Option>
              {awardTracks.map((track) => (
                <Select.Option value={track}>{track}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Card>
      );
    } else {
      return (
        <Card
          style={{
            boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
            margin: "auto",
            maxWidth: "800px",
          }}
        >
          {" "}
          <Tag color="blue">Award Track</Tag>
          <p className="small_text">
            You can choose an award track you want to compete in. This is
            optional.
          </p>
          <p>
            You can choose an award track once a presenting author is selected.
          </p>
        </Card>
      );
    }
  };
  renderSubmitButton = () => {
    if (this.state.lifecycle === "SUBMITTED") {
      return null;
      // add withdrawn later.
    }
    if (!this.state.authors.length) {
      return <Alert message="Please add authors." type="error" showIcon />;
    }
    if (this.state.authors.length > 5) {
      return (
        <Alert
          message="Cannot have more than 5 authors."
          type="error"
          showIcon
        />
      );
    }
    if (!this.state.presentingAuthorId) {
      return (
        <Alert message="No presenting author selected." type="error" showIcon />
      );
    }
    if (
      !this.state.title ||
      !this.state.content.objectives ||
      !this.state.content.methods ||
      !this.state.content.results ||
      !this.state.content.conclusion
    ) {
      return (
        <Alert message="All fields must be filled." type="error" showIcon />
      );
    }
    if (!this.state.termsAgreed) {
      return (
        <Alert message="You must agree to the rules." type="error" showIcon />
      );
    }
    return (
      <Button
        disabled={this.state.isSaving}
        loading={this.state.isSaving}
        type="primary"
        htmlType="submit"
        icon={<CheckCircleOutlined />}
        style={{ backgroundColor: "green", borderColor: "green" }}
      >
        Submit
      </Button>
    );
  };
  renderBestViewedOnDesktopTag = () => {
    if (!this.state.isDesktop) {
      return (
        <Tag color="red">
          This page is best viewed on a desktop or laptop, or in landscape
          orientation.
        </Tag>
      );
    }
  };
  render() {
    return (
      <div>
        <Space direction="vertical" size="middle">
          <h3 style={{ marginBottom: "20px" }}>
            Poster Submission{" "}
            <Tag color={this.colorMap[this.state.lifecycle]}>
              {_.startCase(_.lowerCase(this.state.lifecycle))}
            </Tag>
          </h3>
          <Tour
            open={this.state.isTourOpen}
            onClose={() => this.setState({ isTourOpen: false })}
            steps={this.tourSteps}
          />
          {this.state.lifecycle === "DRAFT" && (
            <Button
              size="small"
              style={{
                backgroundColor: "#72CA9B",
                borderColor: "#72CA9B",
              }}
              icon={<QuestionCircleOutlined />}
              onClick={() => {
                this.setState({ isTourOpen: true });
              }}
            >
              Quick Tutorial
            </Button>
          )}
          {this.renderBestViewedOnDesktopTag()}

          <Form
            onFinish={this.handleSubmit}
            disabled={
              ["WITHDRAWN", "SUBMITTED"].includes(this.state.lifecycle) ||
              this.state.isSaving
            }
          >
            <Space direction="vertical" size="middle">
              {this.state.lifecycle === "DRAFT" ? (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <Tag color="blue">Structured Abstract</Tag>
                  <p className="small_text">
                    You can enter the abstract of your poster here.
                  </p>

                  <Form.Item
                    label="Title"
                    labelCol={{ span: 24 }}
                    rules={[
                      { required: true, message: "Please input the title!" },
                    ]}
                    help="Enter the title of your poster."
                  >
                    <Input
                      ref={this.refTitle}
                      name="title"
                      value={this.state.title}
                      status={
                        this.state.title
                          ? this.state.title.length > 100
                            ? "error"
                            : this.state.title.length > 120
                            ? "warning"
                            : ""
                          : ""
                      }
                      onChange={(e) =>
                        this.setState({
                          title: e.target.value,
                        })
                      }
                      maxLength={125}
                      showCount
                    />
                  </Form.Item>
                  <Form.Item
                    label="Aims & Objectives"
                    labelCol={{ span: 24 }}
                    rules={[
                      {
                        required: true,
                        message: "Please input the objectives!",
                      },
                    ]}
                    help="State the objectives of your poster."
                  >
                    <Input.TextArea
                      ref={this.refIntroduction}
                      status={
                        this.state.content.objectives
                          ? this.state.content.objectives.length > 495
                            ? "error"
                            : this.state.content.objectives.length > 400
                            ? "warning"
                            : ""
                          : ""
                      }
                      name="objectives"
                      value={this.state.content.objectives}
                      onChange={(e) => {
                        let content = this.state.content;
                        content.objectives = e.target.value;
                        this.setState({ content: content });
                      }}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Materials & Methods"
                    labelCol={{ span: 24 }}
                    rules={[
                      { required: true, message: "Please input the methods!" },
                    ]}
                    help="Describe the methods used in your poster."
                  >
                    <Input.TextArea
                      status={
                        this.state.content.methods
                          ? this.state.content.methods.length > 495
                            ? "error"
                            : this.state.content.methods.length > 400
                            ? "warning"
                            : ""
                          : ""
                      }
                      name="methods"
                      value={this.state.content.methods}
                      onChange={(e) => {
                        let content = this.state.content;
                        content.methods = e.target.value;
                        this.setState({ content: content });
                      }}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Results"
                    labelCol={{ span: 24 }}
                    rules={[
                      { required: true, message: "Please input the results!" },
                    ]}
                    help="Present the results of your poster."
                    validateStatus={this.state.resultsStatus}
                  >
                    <Input.TextArea
                      name="results"
                      value={this.state.content.results}
                      onChange={(e) => {
                        let content = this.state.content;
                        content.results = e.target.value;
                        this.setState({ content: content });
                      }}
                      status={
                        this.state.content.results
                          ? this.state.content.results.length > 695
                            ? "error"
                            : this.state.content.results.length > 625
                            ? "warning"
                            : ""
                          : ""
                      }
                      showCount
                      maxLength={700}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Conclusion"
                    labelCol={{ span: 24 }}
                    rules={[
                      {
                        required: true,
                        message: "Please input the conclusion!",
                      },
                    ]}
                    help="Write the conclusion of your poster."
                    validateStatus={this.state.conclusionStatus}
                  >
                    <Input.TextArea
                      status={
                        this.state.content.conclusion
                          ? this.state.content.conclusion.length > 495
                            ? "error"
                            : this.state.content.conclusion.length > 400
                            ? "warning"
                            : ""
                          : ""
                      }
                      name="conclusion"
                      value={this.state.content.conclusion}
                      onChange={(e) => {
                        let content = this.state.content;
                        content.conclusion = e.target.value;
                        this.setState({ content: content });
                      }}
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                </Card>
              ) : (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <h4>{this.state.title}</h4>{" "}
                  <p>
                    <b>Aims & Objectives:</b>:{" "}
                    {this.state.content.objectives.split("\n").map((each) => (
                      <p>{each}</p>
                    ))}
                  </p>
                  <p>
                    <b>Materials & Methods</b>:{" "}
                    {this.state.content.methods.split("\n").map((each) => (
                      <p>{each}</p>
                    ))}
                  </p>
                  <p>
                    <b>Results</b>:{" "}
                    {this.state.content.results.split("\n").map((each) => (
                      <p>{each}</p>
                    ))}
                  </p>
                  <p>
                    <b>Conclusion</b>:{" "}
                    {this.state.content.conclusion.split("\n").map((each) => (
                      <p>{each}</p>
                    ))}
                  </p>
                </Card>
              )}
              {this.state.lifecycle === "DRAFT" ? (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <Tag color="blue">Authors</Tag>
                  <p className="small_text">
                    Each submission can have up to 4 co-authors, alongside a
                    presenting author.
                  </p>
                  <div ref={this.refAuthors}>
                    <Form.Item label="Authors" labelCol={{ span: 24 }}>
                      <AutoComplete
                        disabled={this.state.authors.length === 5}
                        popupMatchSelectWidth={true}
                        style={{ width: 200 }}
                        options={this.state.searchResults.map((x) => ({
                          label: (
                            <Space direction="vertical" size="small">
                              <Tag
                                color={
                                  x.searchresultsrc === "SRC_OSWB"
                                    ? "magenta"
                                    : "blue"
                                }
                              >
                                {x.full_name}
                              </Tag>
                              {x.membership_id && <Tag>{x.membership_id}</Tag>}
                            </Space>
                          ),
                          value: `${x.full_name} - ${x.membership_id}`,
                          delegate: x,
                        }))}
                        onSearch={this.handleSearch}
                        placeholder="Search for an author"
                        onSelect={(value, option) => {
                          this.handleAddAuthorFromSearch(option.delegate);
                        }}
                      />
                      {this.state.isSearching && <Spin />}{" "}
                      <Button
                        disabled={this.state.authors.length === 5}
                        type="default"
                        onClick={() => {
                          let dd = this.props.loggedInUser;
                          if (this.state.authors.length === 5) {
                            notification.error({
                              message: "Cannot add more than 5 authors.",
                            });
                          }
                          let authors = this.state.authors;
                          if (authors.find((x) => x.delegateId === dd._id)) {
                            notification.error({
                              message: "This author is already added.",
                              description: "Please select another author.",
                            });
                            return;
                          }
                          this.setState({
                            authors: [
                              ...this.state.authors,
                              {
                                delegateId: dd._id,
                                name: dd.name,
                                email: dd.email,
                                mobile: dd.mobile,
                                isAgeBelow40: dd.dob
                                  ? isBelow40(dd.dob)
                                  : false,
                                role: "Author",
                              },
                            ],
                          });
                        }}
                      >
                        Add Myself
                      </Button>
                      <Button
                        disabled={this.state.authors.length === 5}
                        ref={this.refAddAuthor}
                        type="default"
                        onClick={() =>
                          this.setState({ showAddUserModal: true })
                        }
                      >
                        Click to add a non-OSWB author
                      </Button>
                      <Modal
                        title="Add New Author"
                        visible={this.state.showAddUserModal}
                        onCancel={() =>
                          this.setState({ showAddUserModal: false })
                        }
                        footer={[
                          <Button
                            key="back"
                            onClick={() =>
                              this.setState({ showAddUserModal: false })
                            }
                          >
                            Cancel
                          </Button>,
                          <Button
                            key="submit"
                            type="primary"
                            loading={this.state.loading}
                            onClick={this.handleAddUser}
                            disabled={
                              !this.state.newUser.name ||
                              !this.state.newUser.email ||
                              !this.state.newUser.phone
                            }
                          >
                            Add
                          </Button>,
                        ]}
                      >
                        <Form>
                          <Form.Item
                            label="Name"
                            rules={[
                              {
                                required: true,
                                message: "Please input the name!",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Name"
                              value={this.state.newUser.name}
                              onChange={(e) =>
                                this.setState({
                                  newUser: {
                                    ...this.state.newUser,
                                    name: e.target.value,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            label="Email"
                            rules={[
                              {
                                required: true,
                                message: "Please input the email!",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Email"
                              value={this.state.newUser.email}
                              onChange={(e) =>
                                this.setState({
                                  newUser: {
                                    ...this.state.newUser,
                                    email: e.target.value,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            label="Phone"
                            rules={[
                              {
                                required: true,
                                message: "Please input the phone number!",
                              },
                            ]}
                          >
                            {" "}
                            <PhoneInput
                              name="Phone"
                              defaultCountry="IN"
                              placeholder="Phone"
                              value={this.state.newUser.phone}
                              onChange={(e) =>
                                this.setState({
                                  newUser: {
                                    ...this.state.newUser,
                                    phone: e,
                                  },
                                })
                              }
                            />
                          </Form.Item>
                        </Form>
                      </Modal>
                      <List
                        locale={{ emptyText: "No authors added." }}
                        itemLayout="horizontal"
                        dataSource={this.state.authors}
                        renderItem={(item) => (
                          <List.Item
                            actions={[
                              <Popconfirm
                                title="Make this author the presenting author? Note that you cannot change this after submission."
                                onConfirm={() =>
                                  this.handleMakePresentingAuthor(item)
                                }
                              >
                                <Button
                                  disabled={
                                    this.state.presentingAuthorId ===
                                    item.delegateId
                                  }
                                >
                                  Make Presenting Author
                                </Button>
                              </Popconfirm>,
                              <Popconfirm
                                title="Sure you want to delete?"
                                onConfirm={() => this.handleDeleteUser(item)}
                              >
                                <Button danger>Delete</Button>
                              </Popconfirm>,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={<UserOutlined />}
                              title={item.name}
                              description={item.role}
                            />
                          </List.Item>
                        )}
                      />
                    </Form.Item>
                  </div>
                </Card>
              ) : (
                <>
                  <Card
                    style={{
                      boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                      margin: "auto",
                      maxWidth: "800px",
                    }}
                  >
                    {" "}
                    <List
                      locale={{ emptyText: "No authors added." }}
                      itemLayout="horizontal"
                      dataSource={this.state.authors}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={<UserOutlined />}
                            title={item.name}
                            description={item.role}
                          />
                        </List.Item>
                      )}
                    />
                    <br />
                    <p>
                      {" "}
                      <b>Award Track</b>: {this.state.awardTrack || "None"}{" "}
                    </p>
                  </Card>
                </>
              )}
              {(this.state.lifecycle === "DRAFT" || this.props.isAdmin) && (
                <div>
                  {" "}
                  <div ref={this.refAwardTrack}>
                    {this.renderAwardSelection()}
                  </div>
                  <div>
                    <input
                      type="button"
                      onClick={() => this.updateAwardTrack()}
                      value={"Update Track"}
                    />
                  </div>
                </div>
              )}{" "}
              {this.state.lifecycle === "DRAFT" && (
                <Card
                  style={{
                    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)",
                    margin: "auto",
                    maxWidth: "800px",
                  }}
                >
                  <Tag color="blue">Finalize submission</Tag>
                  <br />
                  <p className="small_text">
                    Ensure that you've read the rules for this submission:{" "}
                  </p>
                  <Form.Item>
                    <Form.Item>
                      <List
                        dataSource={terms}
                        renderItem={(item) => <List.Item>{item}</List.Item>}
                      />
                      <Checkbox
                        ref={this.refTerms}
                        onChange={(e) =>
                          this.setState({ termsAgreed: e.target.checked })
                        }
                      >
                        I have read and understood these rules.
                      </Checkbox>
                    </Form.Item>
                    <br />
                    {this.renderSubmitButton()}
                    {this.state.lifecycle === "DRAFT" && (
                      <Button
                        onClick={this.handleSaveDraft}
                        ref={this.refDraft}
                      >
                        Save as Draft
                      </Button>
                    )}
                  </Form.Item>
                </Card>
              )}
            </Space>
          </Form>
        </Space>
      </div>
    );
  }
}

export default PosterSubmissionForm;
